//import ApiService from "@/core/services/ApiService";
import JwtService from "@/core/services/JwtService";
import OidcService from "@/core/services/OidcService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";
import mAxiosApi from "@/api";

export interface User {
  uti_c_nom: string;
  uti_c_prenom: string;
  uti_c_mail: string;
  uti_n_seq: string;
  oidc_sub?: string;
  societeCodeArk?: string;
  refCodeArk?: string;
}

export interface UserAuthInfo {
  errors: Array<string>;
  user: User;
  isAuthenticated: boolean;
}

@Module
export default class AuthModule extends VuexModule implements UserAuthInfo {
  errors = [];
  user = {} as User;
  isAuthenticated = !!JwtService.getToken();

  /**
   * Get current user object
   * @returns User
   */
  get currentUser(): User {
    return this.user;
  }

  /**
   * Verify user authentication
   * @returns boolean
   */
  get isUserAuthenticated(): boolean {
    return this.isAuthenticated;
  }

  /**
   * Get authentification errors
   * @returns array
   */
  get getErrors(): Array<string> {
    return this.errors;
  }

  @Mutation
  [Mutations.SET_ERROR](error) {
    this.errors = error;
  }

  @Mutation
  [Mutations.SET_AUTH](user) {
    this.isAuthenticated = true;
    localStorage.setItem("lang", "fr");
    this.user = user;
    this.errors = [];
    if (user.uti_n_seq) {
      JwtService.saveToken(user.uti_n_seq);
    }
  }

  @Mutation
  [Mutations.SET_USER](user) {
    this.user = user;
  }

  @Mutation
  [Mutations.SET_PASSWORD](password) {
    this.user.uti_c_mail = password;
  }

  @Mutation
  [Mutations.PURGE_AUTH]() {
    this.isAuthenticated = false;
    this.user = {} as User;
    this.errors = [];
    JwtService.destroyToken();
  }

  // Classic login (username/password)
  @Action
  async [Actions.LOGIN](credentials) {
    await this.context.commit(Mutations.PURGE_AUTH);
    return new Promise<void>((resolve, reject) => {
      mAxiosApi
        .post("login", credentials)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_AUTH, data);
          resolve();
        })
        .catch(() => {
          this.context.commit(Mutations.SET_ERROR, "Erreur");
          reject();
        });
    });
  }

  // OIDC login (redirects to Zitadel)
  @Action
  async [Actions.OIDC_LOGIN](payload: { codeArk: string; returnPath?: string }) {
    await OidcService.signinRedirect(payload.codeArk, payload.returnPath);
  }

  // Called after OIDC redirect callback to hydrate the store
  @Action
  async [Actions.OIDC_CALLBACK]() {
    const oidcUser = await OidcService.getUser();
    if (!oidcUser || oidcUser.expired) {
      this.context.commit(Mutations.PURGE_AUTH);
      return;
    }
    try {
      const { data } = await mAxiosApi.get("me");
      this.context.commit(Mutations.SET_AUTH, data);
    } catch {
      this.context.commit(Mutations.PURGE_AUTH);
    }
  }

  @Action
  async [Actions.LOGOUT]() {
    const isOidc = await OidcService.isAuthenticated();
    mAxiosApi.post("/logout").catch(() => { /* ignore */ });
    this.context.commit(Mutations.PURGE_AUTH);
    if (isOidc) {
      await OidcService.removeUser();
      // signoutRedirect will navigate away from the page
      try {
        await OidcService.signoutRedirect();
      } catch {
        // If signout redirect fails, stay on current page
      }
    }
  }

  @Action
  [Actions.REGISTER](credentials) {
    return new Promise<void>((resolve, reject) => {
      mAxiosApi
        .post("registration", credentials)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_AUTH, data);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response.data.errors);
          reject();
        });
    });
  }

  @Action
  [Actions.FORGOT_PASSWORD](payload) {
    return new Promise<void>((resolve, reject) => {
      mAxiosApi
        .post("forgot_password", payload)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_AUTH, data);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response.data.errors);
          reject();
        });
    });
  }

  @Action
  async [Actions.VERIFY_AUTH]() {
    // Check OIDC first
    const isOidc = await OidcService.isAuthenticated();
    if (isOidc) {
      if (!this.isAuthenticated) {
        // OIDC session exists but store not hydrated yet
        try {
          const { data } = await mAxiosApi.get("me");
          this.context.commit(Mutations.SET_AUTH, data);
        } catch {
          this.context.commit(Mutations.PURGE_AUTH);
        }
      }
      return;
    }

    // Fallback to classic session check
    if (JwtService.getToken()) {
      mAxiosApi
        .get("me")
        .then(({ data }) => {
          this.context.commit(Mutations.SET_AUTH, data);
        })
        .catch(() => {
          this.context.commit(Mutations.PURGE_AUTH);
        });
    } else {
      this.context.commit(Mutations.PURGE_AUTH);
    }
  }

  @Action
  [Actions.UPDATE_USER](payload) {
    return new Promise<void>((resolve, reject) => {
      mAxiosApi
        .post("update_user", payload)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_USER, data);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response.data.errors);
          reject();
        });
    });
  }
}
